import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const UnitConversionCalculator = () => {
  const [conversionType, setConversionType] = useState('length');
  const [inputValue, setInputValue] = useState('');
  const [inputUnit, setInputUnit] = useState('meters');
  const [outputUnit, setOutputUnit] = useState('kilometers');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    setError('');
  };

  const handleConversionTypeChange = (e) => {
    setConversionType(e.target.value);
    setInputUnit('');
    setOutputUnit('');
  };

  const handleInputUnitChange = (e) => {
    setInputUnit(e.target.value);
  };

  const handleOutputUnitChange = (e) => {
    setOutputUnit(e.target.value);
  };

  const convertUnits = () => {
    if (!inputValue) {
      setError('Please enter a value to convert.');
      return;
    }

    const value = parseFloat(inputValue);
    if (isNaN(value)) {
      setError('Please enter a valid number.');
      return;
    }

    let convertedValue = 0;

    switch (conversionType) {
      case 'length':
        convertedValue = convertLength(value, inputUnit, outputUnit);
        break;
      case 'weight':
        convertedValue = convertWeight(value, inputUnit, outputUnit);
        break;
      case 'volume':
        convertedValue = convertVolume(value, inputUnit, outputUnit);
        break;
      default:
        setError('Please select a valid conversion type.');
        return;
    }

    setResult(convertedValue.toFixed(4));

    // Update chart data
    setChartData({
      labels: [inputUnit, outputUnit],
      datasets: [
        {
          label: 'Conversion Result',
          data: [value, convertedValue],
          backgroundColor: ['rgba(75, 192, 192, 0.5)', 'rgba(54, 162, 235, 0.5)'],
          borderColor: ['rgba(75, 192, 192, 1)', 'rgba(54, 162, 235, 1)'],
          borderWidth: 1
        }
      ]
    });
  };

  const convertLength = (value, fromUnit, toUnit) => {
    const lengthConversionFactors = {
      meters: 1,
      kilometers: 1000,
      feet: 0.3048,
      miles: 1609.34,
      inches: 0.0254,
      yards: 0.9144
    };

    return (value * lengthConversionFactors[fromUnit]) / lengthConversionFactors[toUnit];
  };

  const convertWeight = (value, fromUnit, toUnit) => {
    const weightConversionFactors = {
      grams: 1,
      kilograms: 1000,
      pounds: 453.592,
      ounces: 28.3495
    };

    return (value * weightConversionFactors[fromUnit]) / weightConversionFactors[toUnit];
  };

  const convertVolume = (value, fromUnit, toUnit) => {
    const volumeConversionFactors = {
      liters: 1,
      milliliters: 0.001,
      gallons: 3.78541,
      quarts: 0.946353,
      pints: 0.473176,
      cups: 0.236588
    };

    return (value * volumeConversionFactors[fromUnit]) / volumeConversionFactors[toUnit];
  };

  return (
    <>
      <Helmet>
        <title>Unit Conversion Calculator | BioLab Tools</title>
        <meta name="description" content="Convert units of length, weight, and volume with ease. Accurate and user-friendly unit conversion calculator." />
        <meta name="keywords" content="unit conversion calculator, length conversion, weight conversion, volume conversion" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-7xl">
          <header className="mb-8 text-center">
            <div className="text-4xl font-bold text-green-600 mb-2">📐</div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">Unit Conversion Calculator</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Easily convert units of length, weight, and volume. Get accurate results with a user-friendly interface.
            </p>
          </header>

          <div className="bg-white p-6 rounded-lg shadow-md max-w-2xl mx-auto">
            <form className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Conversion Type</label>
                <select
                  name="conversionType"
                  value={conversionType}
                  onChange={handleConversionTypeChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="length">Length</option>
                  <option value="weight">Weight</option>
                  <option value="volume">Volume</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Value</label>
                <input
                  type="number"
                  value={inputValue}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter value to convert"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">From Unit</label>
                <select
                  name="inputUnit"
                  value={inputUnit}
                  onChange={handleInputUnitChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  {conversionType === 'length' && (
                    <>
                      <option value="meters">Meters</option>
                      <option value="kilometers">Kilometers</option>
                      <option value="feet">Feet</option>
                      <option value="miles">Miles</option>
                      <option value="inches">Inches</option>
                      <option value="yards">Yards</option>
                    </>
                  )}
                  {conversionType === 'weight' && (
                    <>
                      <option value="grams">Grams</option>
                      <option value="kilograms">Kilograms</option>
                      <option value="pounds">Pounds</option>
                      <option value="ounces">Ounces</option>
                    </>
                  )}
                  {conversionType === 'volume' && (
                    <>
                      <option value="liters">Liters</option>
                      <option value="milliliters">Milliliters</option>
                      <option value="gallons">Gallons</option>
                      <option value="quarts">Quarts</option>
                      <option value="pints">Pints</option>
                      <option value="cups">Cups</option>
                    </>
                  )}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">To Unit</label>
                <select
                  name="outputUnit"
                  value={outputUnit}
                  onChange={handleOutputUnitChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  {conversionType === 'length' && (
                    <>
                      <option value="meters">Meters</option>
                      <option value="kilometers">Kilometers</option>
                      <option value="feet">Feet</option>
                      <option value="miles">Miles</option>
                      <option value="inches">Inches</option>
                      <option value="yards">Yards</option>
                    </>
                  )}
                  {conversionType === 'weight' && (
                    <>
                      <option value="grams">Grams</option>
                      <option value="kilograms">Kilograms</option>
                      <option value="pounds">Pounds</option>
                      <option value="ounces">Ounces</option>
                    </>
                  )}
                  {conversionType === 'volume' && (
                    <>
                      <option value="liters">Liters</option>
                      <option value="milliliters">Milliliters</option>
                      <option value="gallons">Gallons</option>
                      <option value="quarts">Quarts</option>
                      <option value="pints">Pints</option>
                      <option value="cups">Cups</option>
                    </>
                  )}
                </select>
              </div>

              <button
                onClick={convertUnits}
                className="w-full bg-green-600 text-white py-3 px-4 rounded-md font-semibold hover:bg-green-700 transition duration-200"
              >
                Convert
              </button>

              {error && (
                <div className="bg-red-100 text-red-700 p-4 rounded-md mt-4">
                  {error}
                </div>
              )}

              {result && (
                <div className="mt-8">
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">Result</h2>
                  <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                    <p className="text-lg font-medium">{inputValue} {inputUnit} is equal to {result} {outputUnit}</p>
                  </div>

                  <div className="mt-8">
                    <h3 className="text-xl font-bold text-gray-800 mb-4">Conversion Visualization</h3>
                    <div className="h-96">
                      <Bar
                        data={chartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: {
                              beginAtZero: true,
                              title: { display: true, text: 'Value' }
                            },
                            x: { title: { display: true, text: 'Unit' } }
                          },
                          plugins: { title: { display: true, text: 'Unit Conversion' } }
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>

          {/* FAQ Section */}
          <div className="mt-16 bg-white p-6 rounded-lg shadow-md max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Frequently Asked Questions (FAQ)</h2>
            <div className="space-y-6">
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">What types of conversions are supported?</h3>
                <p className="text-gray-600">
                  The Unit Conversion Calculator supports conversions for length, weight, and volume. You can convert between various units within each category.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">How accurate are the conversions?</h3>
                <p className="text-gray-600">
                  The conversions are highly accurate, using standard conversion factors. However, always verify critical conversions with multiple sources.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Can I convert multiple units at once?</h3>
                <p className="text-gray-600">
                  Currently, the calculator supports one conversion at a time. For multiple conversions, repeat the process for each unit pair.
                </p>
              </div>
            </div>
          </div>

          {/* How to Use Section */}
          <div className="mt-16 bg-white p-6 rounded-lg shadow-md max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">How to Use the Unit Conversion Calculator</h2>
            <div className="space-y-8">
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Select Conversion Type</h3>
                <p className="text-gray-600">
                  Choose the type of conversion you need: length, weight, or volume.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Enter Value</h3>
                <p className="text-gray-600">
                  Input the value you want to convert.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Select Units</h3>
                <p className="text-gray-600">
                  Choose the units for conversion: the unit of the input value and the desired output unit.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 4: Convert</h3>
                <p className="text-gray-600">
                  Click the "Convert" button to get the result.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 5: View Result and Visualization</h3>
                <p className="text-gray-600">
                  The result will be displayed, along with a bar chart visualizing the conversion.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UnitConversionCalculator;